/**
 * Histogram chart with clickable bins.
 *
 * Measures container width via ResizeObserver. Shows x-axis min/max labels and y-axis max label.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useTheme, fontSize } from '@fti/design-system';
import type { HistogramProps } from '../contracts';
import { formatNumber } from '../format';

export function Histogram({ bins, height = 120, formatX = formatNumber, onBinClick }: HistogramProps) {
  const { theme } = useTheme();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(320);

  // Measure container width
  useEffect(() => {
    if (!wrapperRef.current) return;

    const observer = new ResizeObserver((entries) => {
      if (entries[0]) {
        setWidth(entries[0].contentRect.width);
      }
    });

    observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, []);

  if (bins.length === 0) {
    return (
      <div ref={wrapperRef} style={{ width: '100%', height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: theme.colors.text.secondary, fontSize: fontSize.xs }}>No data</span>
      </div>
    );
  }

  const maxCount = Math.max(...bins.map((b) => b.count), 1);
  const padding = { top: 16, right: 8, bottom: 24, left: 8 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  const barWidth = chartWidth / bins.length;
  const gap = Math.max(1, barWidth * 0.1);
  const effectiveBarWidth = Math.max(barWidth - gap, 1);

  const clickable = !!onBinClick;

  return (
    <div ref={wrapperRef} style={{ width: '100%' }}>
      <svg width={width} height={height}>
        {/* Y-axis max label */}
        <text
          x={padding.left}
          y={padding.top - 4}
          fontSize={fontSize.xs}
          fill={theme.colors.text.secondary}
          textAnchor="start"
        >
          {maxCount}
        </text>

        {/* Bars */}
        <g transform={`translate(${padding.left}, ${padding.top})`}>
          {bins.map((bin, i) => {
            const barHeight = (bin.count / maxCount) * chartHeight;
            const x = i * barWidth;
            const y = chartHeight - barHeight;
            const label = `${formatX(bin.x0)} – ${formatX(bin.x1)}: ${bin.count}`;
            const defaultFill = theme.colors.primary[400];
            const hoverFill = theme.colors.primary[600];

            if (clickable) {
              return (
                <g key={i}>
                  <rect
                    x={x}
                    y={y}
                    width={effectiveBarWidth}
                    height={barHeight}
                    fill={defaultFill}
                    style={{ cursor: 'pointer', transition: 'fill 0.15s' }}
                    tabIndex={0}
                    role="button"
                    aria-label={label}
                    onClick={() => onBinClick(bin)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onBinClick(bin);
                      }
                    }}
                    onMouseEnter={(e) => {
                      (e.target as SVGRectElement).setAttribute('fill', hoverFill);
                    }}
                    onMouseLeave={(e) => {
                      (e.target as SVGRectElement).setAttribute('fill', defaultFill);
                    }}
                  >
                    <title>{label}</title>
                  </rect>
                </g>
              );
            }

            return (
              <rect key={i} x={x} y={y} width={effectiveBarWidth} height={barHeight} fill={defaultFill}>
                <title>{label}</title>
              </rect>
            );
          })}
        </g>

        {/* X-axis labels */}
        <g transform={`translate(${padding.left}, ${height - padding.bottom + 8})`}>
          <text x={0} y={0} fontSize={fontSize.xs} fill={theme.colors.text.secondary} textAnchor="start">
            {formatX(bins[0].x0)}
          </text>
          <text
            x={chartWidth}
            y={0}
            fontSize={fontSize.xs}
            fill={theme.colors.text.secondary}
            textAnchor="end"
          >
            {formatX(bins[bins.length - 1].x1)}
          </text>
        </g>
      </svg>
    </div>
  );
}
